import { Fragment, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { cn } from 'src/lib/utils';
import { getStepTaskResults } from '../helpers/data-job-step-tasks.helpers';
import type { DataJobStepRun } from '../types/data-jobs.types';
import { DataJobStatusBadgeV2 } from './data-job-status-badge-v2';

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

type Props = {
  steps: DataJobStepRun[];
};

export function DataJobStepResultsTable({ steps }: Props) {
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            {['', 'Step', 'Status', 'Duration', 'Rows', 'Error'].map((header) => (
              <TableHead
                key={header || 'expand'}
                className={portalDs.tableHeadCell}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {steps.map((step) => {
            const tasks = getStepTaskResults(step);
            const hasTasks = tasks.length > 0;
            const isOpen = expandedStepId === step.id;

            return (
              <Fragment key={step.id}>
                <TableRow
                  className={cn(
                    portalDs.tableBodyRow,
                    hasTasks && 'cursor-pointer',
                  )}
                  onClick={hasTasks ? () => {
                    setExpandedStepId((current) => (
                      current === step.id ? null : step.id
                    ));
                  } : undefined}
                >
                  <TableCell className={portalDs.tableCell}>
                    {hasTasks ? (
                      <span
                        className="flex size-6 items-center justify-center"
                        aria-hidden="true"
                      >
                        <ChevronRight
                          className={cn(
                            'size-4 text-[#6B6F80] transition-transform',
                            isOpen && 'rotate-90',
                          )}
                        />
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    {step.label}
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    <DataJobStatusBadgeV2 status={step.status} />
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {formatDuration(step.duration_seconds)}
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {step.row_count ?? '—'}
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'max-w-xs truncate')}>
                    {step.error_message ?? '—'}
                  </TableCell>
                </TableRow>
                {isOpen && hasTasks ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="bg-[#F7F8FA] p-0"
                    >
                      <div className="px-4 py-3">
                        <p className={cn(portalDs.bodySm, 'mb-2 font-medium text-[#252840]')}>
                          Seat / table tasks
                        </p>
                        <Table>
                          <TableHeader>
                            <TableRow className={portalDs.tableHead}>
                              {['Task', 'State', 'Rows', 'Error'].map((header) => (
                                <TableHead
                                  key={header}
                                  className={portalDs.tableHeadCell}
                                >
                                  {header}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tasks.map((task) => (
                              <TableRow
                                key={`${step.id}-${task.name}`}
                                className={portalDs.tableBodyRow}
                              >
                                <TableCell className={portalDs.tableCell}>
                                  {task.name}
                                </TableCell>
                                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                                  {task.state}
                                </TableCell>
                                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                                  {task.rowCount ?? '—'}
                                </TableCell>
                                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'max-w-md break-words')}>
                                  {task.error ?? '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
